import Loader from "@src/components/Loader/Loader";
import { getMarketplaceDisplayName } from "@src/constants/marketplaceEventUrls";
import { PipelineHealthRow } from "./pipelineHealth.types";
import {
	formatDateTime,
	formatDurationMs,
	getJobTypeLabel,
	getMarketplaceLabel,
	getRowStateKey,
	getRowStateLabel,
	getRunStatusLabel,
} from "./pipelineHealthUtils";
import { usePipelineHealthRuns } from "./usePipelineHealthRuns";
import {
	ExpandToggle,
	PerMarketplaceList,
	RowSummary,
	RunRow,
	RunsDetail,
	RunsHeaderRow,
	StateMessage,
	StatusBadge,
	TruncationNote,
} from "./PipelineHealthPopup.styled";

interface DateRange {
	from: string;
	to: string;
}

interface PipelineHealthTableRowProps {
	row: PipelineHealthRow;
	isExpanded: boolean;
	onToggle: () => void;
	range: DateRange | null;
}

export const PipelineHealthTableRow = ({ row, isExpanded, onToggle, range }: PipelineHealthTableRowProps) => {
	const { runs, total, hasMore, loading, error } = usePipelineHealthRuns({
		enabled: isExpanded,
		jobType: row.jobType,
		marketplace: row.marketplace,
		from: range?.from ?? null,
		to: range?.to ?? null,
	});

	return (
		<div>
			<RowSummary type="button" onClick={onToggle}>
				<ExpandToggle $expanded={isExpanded}>&#9656;</ExpandToggle>
				<span>{getJobTypeLabel(row.jobType)}</span>
				<span>{getMarketplaceLabel(row.marketplace)}</span>
				<StatusBadge $stateKey={getRowStateKey(row)}>{getRowStateLabel(row)}</StatusBadge>
				<span>{row.latestRun ? formatDateTime(row.latestRun.startedAt) : "-"}</span>
				<span>{row.latestRun?.finishedAt ? formatDateTime(row.latestRun.finishedAt) : "-"}</span>
				<span>{row.latestRun ? formatDurationMs(row.latestRun.durationMs) : "-"}</span>
			</RowSummary>

			{isExpanded && (
				<RunsDetail>
					{loading && (
						<StateMessage>
							<Loader controlSize="sm" />
						</StateMessage>
					)}
					{!loading && error && <StateMessage>Run history could not be loaded. Please try again.</StateMessage>}
					{!loading && !error && runs.length === 0 && <StateMessage>No runs in the selected date range.</StateMessage>}
					{!loading && !error && runs.length > 0 && (
						<>
							<RunsHeaderRow>
								<span>Started</span>
								<span>Finished</span>
								<span>Duration</span>
								<span>Outcome</span>
								<span>{row.jobType === "reprocessing" ? "Per marketplace" : ""}</span>
							</RunsHeaderRow>
							{runs.map((run, index) => (
								<RunRow key={`${run.startedAt}-${index}`}>
									<span>{formatDateTime(run.startedAt)}</span>
									<span>{run.finishedAt ? formatDateTime(run.finishedAt) : "-"}</span>
									<span>{formatDurationMs(run.durationMs)}</span>
									<StatusBadge $stateKey={run.status}>{getRunStatusLabel(run.status)}</StatusBadge>
									{row.jobType === "reprocessing" && run.perMarketplace ? (
										<PerMarketplaceList>
											{Object.entries(run.perMarketplace).map(([marketplace, stats]) => (
												<span key={marketplace}>
													{getMarketplaceDisplayName(marketplace)}: {stats.candidatesEvaluated ?? 0} evaluated,{" "}
													{stats.matched ?? 0} matched
												</span>
											))}
										</PerMarketplaceList>
									) : (
										<span />
									)}
								</RunRow>
							))}
							{hasMore && (
								<TruncationNote>
									Showing {runs.length} of {total} runs. Narrow the date range to see the rest.
								</TruncationNote>
							)}
						</>
					)}
				</RunsDetail>
			)}
		</div>
	);
};
